import { useState } from "react";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Button } from "@/components/ui/button";
import { Textarea } from "@/components/ui/textarea";
import { toast } from "sonner";
import { Loader2 } from "lucide-react";
import { RadioGroup, RadioGroupItem } from "@/components/ui/radio-group";
import { Label } from "@/components/ui/label";
import { ScrollArea } from "@/components/ui/scroll-area";
import { supabase } from "@/integrations/supabase/client";
import { performLegalWebSearch, formatSearchResultsForAI } from "@/services/legalSearchService";
import DOMPurify from "dompurify";

interface CountryLaw {
  name: string;
  url: string;
}

const countryLaws: CountryLaw[] = [
  { name: "Australia", url: "https://www.legislation.gov.au/" },
  { name: "Canada", url: "https://laws-lois.justice.gc.ca/" },
  { name: "France", url: "https://www.legifrance.gouv.fr/" },
  { name: "Germany", url: "https://www.gesetze-im-internet.de/" },
  { name: "India", url: "https://www.indiacode.nic.in/" },
  { name: "Ireland", url: "http://www.irishstatutebook.ie/" },
  { name: "Italy", url: "https://www.normattiva.it/" },
  { name: "Japan", url: "http://www.japaneselawtranslation.go.jp/" },
  { name: "Mexico", url: "https://www.dof.gob.mx/" },
  { name: "New Zealand", url: "https://www.legislation.govt.nz/" },
  { name: "Singapore", url: "https://sso.agc.gov.sg/" },
  { name: "South Africa", url: "https://www.acts.co.za/" },
  { name: "United Kingdom", url: "https://www.legislation.gov.uk/" },
  { name: "United States", url: "https://uscode.house.gov/" }
];

export const LocalLawsTab = () => {
  const [selectedCountry, setSelectedCountry] = useState<string>("");
  const [question, setQuestion] = useState<string>("");
  const [response, setResponse] = useState<string>("");
  const [isLoading, setIsLoading] = useState(false);
  const [aiEngine, setAiEngine] = useState<"openai" | "deepseek">("openai");


  const handleSendQuestion = async () => {
    if (!selectedCountry) {
      toast.error("Please select a country first");
      return;
    }
    if (!question.trim()) {
      toast.error("Please enter a question");
      return;
    }

    setIsLoading(true);
    setResponse("");

    const selectedLaw = countryLaws.find(law => law.name === selectedCountry);
    
    try {
      // First, perform web search for real-time legal information
      console.log('Performing web search for:', question, selectedCountry);
      const webSearchResult = await performLegalWebSearch(question, selectedCountry, 'Local Laws');
      
      let searchContext = '';
      if (webSearchResult.searchResults && webSearchResult.searchResults.length > 0) {
        searchContext = `Recent search results from official legal sources:
${formatSearchResultsForAI(webSearchResult.searchResults)}

`;
        console.log('Web search found', webSearchResult.searchResults.length, 'results');
      } else {
        console.log('No web search results found, using AI knowledge base only');
        if (webSearchResult.error) {
          console.warn('Web search error:', webSearchResult.error);
        }
      }

      const { data: aiData, error: aiError } = await supabase.functions.invoke('ai-chat-completion', {
        body: {
          messages: [
            { 
              role: 'system', 
              content: `You are a legal expert assistant. ${searchContext ? 'Use the provided recent search results from official legal sources as your primary reference, supplemented by your knowledge of' : 'Use your knowledge of'} ${selectedCountry} laws from official legal resources like ${selectedLaw?.url}. Answer in a layman manner so that it is easier to understand the laws of the country. If recent search results are provided, prioritize that information and cite the sources.`
            },
            { role: 'user', content: `${searchContext}Question: ${question}` }
          ],
          model: 'gpt-4o-mini'
        }
      });

      if (aiError) {
        throw new Error(`AI API error: ${aiError.message}`);
      }

      const aiResponse = aiData?.choices?.[0]?.message?.content;
      if (aiResponse) {
        toast.success("Response received with real-time legal data!");
        setResponse(aiResponse);
        console.log("AI Response with web search data:", aiResponse);
      } else {
        throw new Error("No response content received");
      }
    } catch (error: any) {
      console.error("Error:", error);
      toast.error(error.message || `Failed to get response from ${aiEngine === "openai" ? "OpenAI" : "DeepSeek"}`);
    } finally {
      setIsLoading(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent<HTMLTextAreaElement>) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSendQuestion();
    }
  };

  const formatResponse = (text: string) => {
    if (!text) return "";

    let normalizedText = text.replace(/\n{2,}/g, '\n');
    const lines = normalizedText.split('\n');
    const formattedLines = lines.map(line => {
      if (line.startsWith('### ')) {
        return `<div class="text-yellow-400 font-bold">${line.substring(4)}</div>`;
      }
      let formattedLine = line;
      while (formattedLine.includes('**')) {
        const startIdx = formattedLine.indexOf('**');
        const endIdx = formattedLine.indexOf('**', startIdx + 2);
        if (endIdx === -1) break;
        const beforeText = formattedLine.substring(0, startIdx);
        const boldText = formattedLine.substring(startIdx + 2, endIdx);
        const afterText = formattedLine.substring(endIdx + 2);
        formattedLine = `${beforeText}<span class="text-blue-400 font-bold">${boldText}</span>${afterText}`;
      }
      return `<div>${formattedLine}</div>`;
    });
    return formattedLines.join('\n');
  };

  return (
    <div className="bg-secondary/50 backdrop-blur-lg border-2 border-primary rounded-lg shadow-lg shadow-purple-500/10 space-y-6 p-6 bg-secondary border h-[580px]">
      <div className="flex gap-8">
        <div className="flex flex-col gap-2">
          <span className="text-white">Select country:</span>
          <div className="w-fit">
            <Select value={selectedCountry} onValueChange={setSelectedCountry}>
              <SelectTrigger className="bg-secondary-light/5 bg-secondary-light/5 border border-primary/20 text-white">
                <SelectValue placeholder="Select a country" />
              </SelectTrigger>
              <SelectContent>
                {countryLaws.map(country => (
                  <SelectItem key={country.name} value={country.name}>
                    {country.name}
                  </SelectItem>
                ))}
              </SelectContent>
            </Select>
          </div>
        </div>

        <div className="flex flex-col gap-2">
          <span className="text-white">Question:</span>
          <div className="flex gap-4">
            <Textarea 
              value={question} 
              onChange={e => setQuestion(e.target.value)} 
              onKeyPress={handleKeyPress} 
              placeholder="Enter your legal question..." 
              className="w-[600px] bg-secondary-light/5 border border-primary/20 rounded-lg bg-background/5 text-white p-2" 
            />
            <Button 
              onClick={handleSendQuestion} 
              disabled={isLoading} 
              className="bg-primary hover:bg-primary/90 self-start text-black rounded-lg"
            >
              Send
            </Button>
          </div>
        </div>
      </div>

      <div className="mt-6">
        <div className="flex items-center justify-between text-primary-light mb-1">
          <div className="flex items-center">
            <span className="mr-1 text-white">Choose AI Engine: </span>
            <RadioGroup 
              className="flex items-center space-x-4 ml-2" 
              value={aiEngine} 
              onValueChange={value => setAiEngine(value as "openai" | "deepseek")}
            >
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="openai" id="openai" />
                <Label htmlFor="openai" className="text-primary-light cursor-pointer">OpenAI</Label>
              </div>
              <div className="flex items-center space-x-2">
                <RadioGroupItem value="deepseek" id="deepseek" />
                <Label htmlFor="deepseek" className="text-primary-light cursor-pointer">DeepSeek</Label>
              </div>
            </RadioGroup>
          </div>
        </div>
        <div className="text-white text-[11px] mb-2">
          (Enhanced with real-time web search from official legal sources. Information provided does not constitute legal advice.)
        </div>
        <ScrollArea className="h-[350px] border border-primary/20 rounded-lg bg-background/5 mb-4 text-white text-[13px] overflow-y-auto">
          {isLoading ? (
            <div className="flex flex-col items-center justify-center h-full">
              <Loader2 className="h-8 w-8 animate-spin text-primary mb-2" />
              <p className="text-primary-light">(Searching for real-time legal data and analyzing with {aiEngine === "openai" ? "OpenAI" : "DeepSeek"})</p>
            </div>
          ) : response ? (
            <div 
              className="p-4 whitespace-pre-wrap max-h-full overflow-y-auto" 
              dangerouslySetInnerHTML={{ __html: DOMPurify.sanitize(formatResponse(response), { ALLOWED_TAGS: ['div','span','br','strong','em','b','i','p','ul','ol','li'], ALLOWED_ATTR: ['class'] }) }} 
            />
          ) : (
            <div className="flex items-center justify-center h-full">
              <p className="text-gray-400 text-center">After selecting a country and providing a question, click on the Send button to get a response enhanced with real-time legal data</p>
            </div>
          )}
        </ScrollArea>
        <p className="text-[12px] text-white">(Note: AI hallucination may occur. Validation of generated information is essential.)</p>
      </div>
    </div>
  );
};
